/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useContext,
} from "react";
import { GlobalLogger } from "./debugLogger";
import {
  CameraPermissionModalConfig,
  ErrorModalT,
} from "../types/Modals/ErrorModal";
import React from "react";
import { ModelPrediction } from "./types/Model";
import { WebToMobileHandOffConfig } from "../components/ErrorModal/components/WebToMobile/WebToMobile";
import { HelpModalConfig } from "../components/ErrorModal/components/HelpModal/HelpModal";
import { RequestPermissionModalConfig } from "../components/RequestingPermissionModal/RequestingPermissionModal";
import { NoCameraModalConfig } from "../types/Modals/NoCameraModal";
import { CardScanApi } from "@cardscan.ai/cardscan-client";
import { LogLevel, CardScanError, NameCase } from "../types/types";
import { InternalOptions } from "../types/scanview.types";
import AdaptiveThreshold from "../utils/AdaptiveThreshold";

export type GlobalContent = {
  logging: LogLevel;
  modelState: any;
  scanDebugState: any;
  adaptiveThreshold: React.MutableRefObject<AdaptiveThreshold | null>;
  scanFreqency: any;
  setScanFreqency: any;
  bufferSize: any;
  setBufferSize: any;
  bufferRef: any;
  debugCanvasBestRef: any;
  bestScore: any;
  bestClass: ModelPrediction | null;
  lastClass: string;
  bestLaplacian: any;
  debugCanvasLatestRef: any;
  latestScore: any;
  latestLaplacian: any;
  debugMenu: boolean;
  videoMetadata: any;
  showCurtain: boolean;
  onCloseBtn: any;
  closeButton: any;
  isMobile: boolean;
  isMobileOnLandscape: boolean;
  stateMachineState: any;
  fullScreen: boolean;
  stateMachineSend: any;
  stateMachineService: any;
  handleRetryButton: () => void;
  cardScanApi: MutableRefObject<CardScanApi<"camel"> | null>;
  showErrorModal: ErrorModalT;
  cameraPermissionModalConfig?: CameraPermissionModalConfig;
  webToMobileHandoffConfig?: WebToMobileHandOffConfig;
  setShowErrorModal: Dispatch<SetStateAction<ErrorModalT>>;
  videoRef: React.RefObject<HTMLVideoElement>;
  setVideoMetadata: Dispatch<SetStateAction<{ [key: string]: any }>>;
  onError?: (error: CardScanError) => void;
  setScanDebugState: React.Dispatch<React.SetStateAction<string>>;
  backsideSupport: boolean;
  helpModalConfig?: HelpModalConfig;
  requestPermissionModalConfig?: RequestPermissionModalConfig;
  noCameraModalConfig?: NoCameraModalConfig;
  webviewVersion?: string | null;
  mobileSdkVersion?: string | null;
  webToMobileTimer: number | null;
  setWebToMobileTimer: Dispatch<SetStateAction<number | null>>;
  changeAriaLiveMessage: (message: string) => void;
  scannerMainContainerRef: React.RefObject<HTMLDivElement>;
  modalRef: React.RefObject<HTMLDivElement>;
  live: boolean;
  internalOptions: InternalOptions;
  metricsDumpRef: MutableRefObject<string[]>;
  scanningGuideMessage: string;
  guideColor: "notDetected" | "detected";
};

export const MyGlobalContext = createContext<GlobalContent>({
  logging: GlobalLogger.startingLogLevel,
  adaptiveThreshold: { current: null },
  modelState: {},
  scanDebugState: {},
  scanFreqency: {},
  setScanFreqency: {},
  bufferSize: {},
  setBufferSize: {},
  bufferRef: {},
  debugCanvasBestRef: {},
  bestScore: {},
  bestClass: null,
  lastClass: "",
  bestLaplacian: {},
  debugCanvasLatestRef: {},
  latestScore: {},
  latestLaplacian: {},
  debugMenu: false,
  videoMetadata: {},
  showCurtain: false,
  onCloseBtn: () => {},
  cardScanApi: { current: null },
  closeButton: null,
  isMobile: false,
  fullScreen: false,
  isMobileOnLandscape: false,
  stateMachineState: {},
  stateMachineSend: () => {},
  stateMachineService: {},
  handleRetryButton: () => {},
  showErrorModal: { show: false, modal: "none" },
  setShowErrorModal: () => {},
  cameraPermissionModalConfig: {},
  webToMobileHandoffConfig: {},
  videoRef: React.createRef<HTMLVideoElement>(),
  setVideoMetadata: () => {},
  setScanDebugState: () => {},

  backsideSupport: false,
  helpModalConfig: {},
  requestPermissionModalConfig: {},
  noCameraModalConfig: {},
  webviewVersion: null,
  mobileSdkVersion: null,
  webToMobileTimer: null,
  setWebToMobileTimer: () => {},
  changeAriaLiveMessage: () => {},
  scannerMainContainerRef: React.createRef<HTMLDivElement>(),
  modalRef: React.createRef<HTMLDivElement>(),
  live: false,
  internalOptions: {},
  metricsDumpRef: { current: [] },
  scanningGuideMessage: "",
  guideColor: "notDetected",
});

export const useGlobalContext = () => useContext(MyGlobalContext);
